import { Box, Card, CardActionArea, IconButton, Stack, Typography } from '@mui/material'
import type { CatalogCardModel } from '@commerce/core'
import { YALO_RADIUS, YALO_SEMANTIC, YALO_SHADOW } from '@commerce/tokens'

interface Props {
  model: CatalogCardModel
  onAdd: (sku: string) => void
  /** Take one unit back out; at quantity 1 this removes the line. */
  onRemoveOne: (sku: string) => void
  onOpen: (sku: string) => void
}

/**
 * One concrete card — not a card-rendering engine. cinnamon's ItemCard needed
 * 739 LOC of defaultProps and 699 LOC of ItemCardChildren because the JSON
 * could ask for any shape. Here the shape is the type.
 *
 * There is no arithmetic and no formatting in this file. Everything it renders
 * was decided by `toCatalogCardModel`, which is why the React Native card can
 * be a different render of the same model rather than a second implementation.
 *
 * The plan draws the title as a `Typography` with an `onClick`. That is a click
 * handler on a `<p>`: no keyboard focus, no role, unreachable without a mouse.
 * `CardActionArea` is MUI's real button, so opening the product detail works
 * from the keyboard and is announced as an action.
 *
 * The design (reference.html:799–822) draws the add control as a round `+`
 * beside the price rather than a labelled button. It keeps its accessible name
 * — a bare `+` announces as "plus" — and it stays *outside* the action area,
 * because a button inside a button is invalid DOM and only one of the two ever
 * receives the click.
 *
 * Once the product is in the cart the design swaps the round `+` for a pill
 * stepper under the price (`p.inCart`, reference.html:816–821): − / quantity /
 * +. It is not `QuantityStepper` on purpose — that component carries a typable
 * draft for the cart page, and a card offers one step at a time, so its `+` is
 * the same "add one" the round button was.
 */
export const CatalogCard = ({ model, onAdd, onRemoveOne, onOpen }: Props) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: `${YALO_RADIUS.lg}px`,
      borderColor: YALO_SEMANTIC.borderSubtle,
      boxShadow: YALO_SHADOW.xs,
      overflow: 'hidden',
      '&:hover': { boxShadow: YALO_SHADOW.sm },
    }}
  >
    <CardActionArea onClick={() => onOpen(model.sku)} aria-label={model.title}>
      <Box
        sx={{
          position: 'relative',
          height: 112,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: YALO_SEMANTIC.bgSurface,
          borderBottom: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
        }}
      >
        <Box
          component="img"
          src={model.imageUrl}
          alt={model.title}
          sx={{ width: '76%', height: '84%', objectFit: 'contain' }}
        />
        {model.promotionBadge && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              px: 1,
              py: '3px',
              borderRadius: `${YALO_RADIUS.pill}px`,
              // The design's promo pill is the brand green at 92%, not a MUI
              // chip: it sits on the product photo and has to stay legible.
              bgcolor: 'rgba(64, 228, 169, 0.92)',
              color: YALO_SEMANTIC.fg1,
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: 1.4,
            }}
          >
            {model.promotionBadge.text}
          </Box>
        )}
      </Box>

      <Box sx={{ px: 1.5, pt: 1.25 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, lineHeight: '16px', minHeight: 32 }}
        >
          {model.title}
        </Typography>
      </Box>
    </CardActionArea>

    <Stack spacing={0.75} sx={{ px: 1.5, pt: 0.75, pb: 1.5, mt: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        {model.showPrice ? (
          <Typography
            sx={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.3px' }}
            color="primary.dark"
          >
            {model.priceText}
          </Typography>
        ) : (
          <Box />
        )}
        {model.quantityInCart === 0 && (
          <IconButton
            onClick={() => onAdd(model.sku)}
            disabled={!model.canAddToCart}
            aria-label={model.isOutOfStock ? `Sin stock: ${model.title}` : `Agregar ${model.title}`}
            sx={{
              width: 34,
              height: 34,
              flex: 'none',
              fontSize: 17,
              fontWeight: 700,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.main', filter: 'brightness(1.08)' },
              '&:active': { transform: 'scale(0.94)' },
              '&.Mui-disabled': { bgcolor: YALO_SEMANTIC.bgSunken, color: YALO_SEMANTIC.fg4 },
            }}
          >
            +
          </IconButton>
        )}
      </Stack>

      {/* The design's in-cart stepper: the quantity lives between its two
          controls, so "what is in the cart" and "change it" are one thing. The
          quantity comes from the cart, joined in by `toCatalogItemInputs`. */}
      {model.quantityInCart > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          role="group"
          aria-label={`Cantidad de ${model.title}`}
          sx={{
            bgcolor: YALO_SEMANTIC.bgSunken,
            borderRadius: `${YALO_RADIUS.pill}px`,
            p: 0.5,
          }}
        >
          <IconButton
            onClick={() => onRemoveOne(model.sku)}
            aria-label={`Quitar uno de ${model.title}`}
            sx={{
              width: 30,
              height: 30,
              fontSize: 15,
              fontWeight: 700,
              bgcolor: YALO_SEMANTIC.bgSurface,
              border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
              color: YALO_SEMANTIC.fg1,
              '&:active': { transform: 'scale(0.94)' },
            }}
          >
            −
          </IconButton>
          <Typography sx={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 14 }}>
            {model.quantityInCart}
          </Typography>
          <IconButton
            onClick={() => onAdd(model.sku)}
            disabled={!model.canAddToCart}
            aria-label={`Agregar ${model.title}`}
            sx={{
              width: 30,
              height: 30,
              fontSize: 15,
              fontWeight: 700,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.main', filter: 'brightness(1.08)' },
              '&:active': { transform: 'scale(0.94)' },
              '&.Mui-disabled': { bgcolor: YALO_SEMANTIC.bgSurface, color: YALO_SEMANTIC.fg4 },
            }}
          >
            +
          </IconButton>
        </Stack>
      )}
    </Stack>
  </Card>
)
